/* Fuel prices · what a litre costs where the buses fill up (2026-10-28), in
   the Fuel page's design. The data and rules: data/fuelPrices.ts. Every fuel
   cost on Trasse is worked out from this list: a station on it prices a fill
   there, anywhere else uses the EPRA ceiling. */
import React, { useState } from 'react';
import PlacesNewAutocomplete from '../../components/PlacesNewAutocomplete';
import { useOrgSubscription } from '../../services/useOrgSubscription';
import { useFleet } from '../data/FleetData';
import { monthWords, PRIMARY_FUEL_STATION_LIMIT, StationFuelType, StationRow, useFuelPrices } from '../data/fuelPrices';
import { ConsoleModal, fieldInput, ModalButtons, ModalField } from '../Modal';
import { usePage, useShell } from '../Shell';
import { Empty, PageBody, PageSkeleton, StaleBanner, ZanaMark, dayMonth, n0, plural, useToast } from '../kit';

const ksh = (n: number | null | undefined) => (n != null && Number.isFinite(n) ? n0(n) : '—');
const town = (t: string | null) => (t ? t.charAt(0).toUpperCase() + t.slice(1) : '');

/** A price typed straight into the table: saved on Enter or when the box loses focus. */
const PriceCell: React.FC<{ value?: number; ceiling: number | null; label: string; onSave: (v: number) => void }> = ({ value, ceiling, label, onSave }) => {
  const [draft, setDraft] = useState<string | null>(null);
  const shown = draft ?? (value != null ? String(value) : '');
  const commit = () => {
    if (draft == null) return;
    const v = parseFloat(draft);
    setDraft(null);
    if (Number.isFinite(v) && v > 0 && v !== value) onSave(v);
  };
  return (
    <div className="row" style={{ gap: 6 }}>
      <span className="sm" style={{ color: 'var(--muted)' }}>Ksh</span>
      <input className="tin num" aria-label={label} inputMode="decimal" value={shown} placeholder={ceiling != null ? String(Math.round(ceiling)) : '—'}
        style={{ width: 92, height: 36 }}
        onChange={(e) => setDraft(e.target.value.replace(/[^\d.]/g, ''))}
        onBlur={commit} onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setDraft(null); }} />
    </div>
  );
};

const AddStation: React.FC<{ apiKey: string; zanaTier: boolean; primaryLeft: number; ceilings: { diesel: number | null; petrol: number | null }; onAdd: (name: string, placeId: string, ft: StationFuelType, price: number) => Promise<{ ok: boolean; message?: string }>; onClose: () => void }> = ({ apiKey, zanaTier, primaryLeft, ceilings, onAdd, onClose }) => {
  const [name, setName] = useState('');
  const [placeId, setPlaceId] = useState('');
  const [ft, setFt] = useState<StationFuelType>('diesel');
  const [price, setPrice] = useState('');
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const p = parseFloat(price);
  const submit = () => {
    setTried(true);
    if (busy || !name.trim() || !(p > 0)) return;
    setBusy(true);
    onAdd(name, placeId, ft, p).then((r) => { if (r.ok) onClose(); else setErr(r.message || 'Couldn’t add it.'); }).finally(() => setBusy(false));
  };
  return (
    <ConsoleModal title="Add a station" onClose={onClose} onSubmit={submit} enterHint="add"
      footer={<ModalButtons onCancel={onClose} primary="Add station" onPrimary={submit} busy={busy} />}>
      <p style={{ margin: 0, color: 'var(--muted)', fontSize: 15 }}>
        A station your buses use, and what a litre costs there.{zanaTier ? ` It becomes one of your primary stations — ${primaryLeft} of ${PRIMARY_FUEL_STATION_LIMIT} left.` : ''}
      </p>
      <ModalField label="Station" error={tried && !name.trim() ? 'Which station?' : null}>
        {(id, ring) => (apiKey
          ? <div style={{ flex: 1, minWidth: 0 }} onFocus={ring.onFocus} onBlur={ring.onBlur}>
              <PlacesNewAutocomplete apiKey={apiKey} value={name} regionCodes={['ke']} placeholder="Shell, Langata Road"
                onChange={(t) => { setName(t); setPlaceId(''); }} onPick={(x) => { setName(x.name); setPlaceId(x.placeId); }}
                inputStyle={{ ...fieldInput, width: '100%' }} />
            </div>
          : <input id={id} {...ring} style={fieldInput} value={name} placeholder="Shell, Langata Road" onChange={(e) => setName(e.target.value)} />)}
      </ModalField>
      <div className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 14, fontWeight: 600 }}>Fuel</span>
          <div className="seg" role="group" aria-label="Fuel">
            {(['diesel', 'petrol'] as StationFuelType[]).map((x) => (
              <button key={x} type="button" className={ft === x ? 'on' : ''} aria-pressed={ft === x} onClick={() => setFt(x)}>{x === 'diesel' ? 'Diesel' : 'Petrol'}</button>
            ))}
          </div>
        </div>
        <ModalField label="Ksh per litre" width={200} error={tried && !(p > 0) ? 'The pump price.' : null}
          hint={ceilings[ft] != null ? `EPRA ceiling ${n0(ceilings[ft] as number)}` : undefined}>
          {(id, ring) => <input id={id} {...ring} style={fieldInput} inputMode="decimal" value={price} placeholder={ceilings[ft] != null ? String(Math.round(ceilings[ft] as number)) : '200'} onChange={(e) => setPrice(e.target.value.replace(/[^\d.]/g, ''))} />}
        </ModalField>
      </div>
      {err && <span role="alert" style={{ color: 'var(--bad)', fontWeight: 600, fontSize: 14 }}>{err}</span>}
    </ConsoleModal>
  );
};

const FuelPrices: React.FC = () => {
  const f = useFleet();
  const sub = useOrgSubscription(f.org.code || null);
  const zanaTier = !!sub.isZanaTier;
  const fp = useFuelPrices(f.org.code, zanaTier);
  const { toast } = useToast();
  const { ask } = useShell();
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState<'epra' | 'nearby' | null>(null);
  const [removing, setRemoving] = useState<StationRow | null>(null);

  usePage({
    key: 'ConsoleFuelPrices', title: 'Fuel prices', parent: { page: 'ConsoleFuel', title: 'Fuel' }, zanaContext: 'Fuel prices',
    zanaIntro: 'I can find the stations near your school and explain the EPRA ceilings.',
    prompts: ['What is the EPRA diesel price this month?', 'Which stations do my buses use?', 'Is any station charging over the ceiling?'],
  });

  const say = (r: { ok: boolean; message?: string }, done?: string) => {
    if (r.ok) { if (r.message || done) toast({ kind: 'success', text: r.message || done || '' }); } else toast({ kind: 'error', text: r.message || 'That didn’t work.' });
  };
  const run = (k: 'epra' | 'nearby') => {
    if (busy) return;
    setBusy(k);
    (k === 'epra' ? fp.refreshEpra() : fp.seedNearby()).then((r) => say(r)).finally(() => setBusy(null));
  };

  const stale = fp.source === 'carried_forward' || fp.source === 'regional_default';
  const ceilingWord = fp.source === 'epra' || fp.lastEpra ? 'EPRA ceiling' : 'Estimate';
  const over = fp.rows.filter((r) => (r.diesel != null && fp.caps.diesel != null && r.diesel > fp.caps.diesel) || (r.petrol != null && fp.caps.petrol != null && r.petrol > fp.caps.petrol));

  return (
    <PageBody loading={f.loading || sub.status === 'loading' || fp.loading} skeleton={<PageSkeleton stats rows={6} />}>
      <div className={'wrap' + (f.error ? ' stale' : '')} style={{ maxWidth: 1040 }}>
        {f.error && <StaleBanner what="prices" since={f.updatedAt} onRetry={f.retry} />}
        <div className="phead">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <h1 className="h1">Fuel prices</h1>
            <p className="sub">{monthWords(fp.month)} · what a litre costs at the stations your buses use. Every fuel cost on Trasse is worked out from these.</p>
          </div>
          <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
            {zanaTier && (
              <button type="button" className="btn b-line" onClick={() => run('epra')} disabled={!!busy}>
                {busy === 'epra' ? <><span className="spin" aria-hidden="true" style={{ width: 14, height: 14 }} />Reading EPRA…</> : 'Refresh EPRA prices'}
              </button>
            )}
            <button type="button" className="btn b-ink" onClick={() => setAdding(true)}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
              Add a station
            </button>
          </div>
        </div>

        {stale && (
          <div className="banner warn" role="status">
            <span style={{ flex: 1 }}>
              <b>{fp.source === 'carried_forward' ? `These are still your ${fp.carriedFrom ? monthWords(fp.carriedFrom) : 'last month’s'} prices.` : 'No prices on file for this fleet yet.'}</b>{' '}
              {fp.source === 'carried_forward' ? 'Fuel costs are estimates until you update them.' : 'Fuel costs use a regional average until you add the stations your buses use.'}
            </span>
          </div>
        )}

        <div className="stats">
          <div className="stat">
            <span className="k">Diesel · {ceilingWord}</span>
            <span className="big"><span className="unit">Ksh</span>{ksh(fp.caps.diesel)}</span>
            <span className="sm" style={{ color: 'var(--faint)' }}>per litre{fp.epraTown ? ` · ${town(fp.epraTown)}` : ''}</span>
          </div>
          <div className="stat">
            <span className="k">Petrol · {ceilingWord}</span>
            <span className="big"><span className="unit">Ksh</span>{ksh(fp.caps.petrol)}</span>
            <span className="sm" style={{ color: 'var(--faint)' }}>per litre{fp.epraTown ? ` · ${town(fp.epraTown)}` : ''}</span>
          </div>
          <div className="stat">
            <span className="k">Stations on your list</span>
            <span className="big">{fp.placeCount}</span>
            <span className="sm" style={{ color: 'var(--faint)' }}>{zanaTier ? `${fp.primaryCount} of ${PRIMARY_FUEL_STATION_LIMIT} primary` : 'aim for the ones your buses use'}</span>
          </div>
          <div className="stat">
            <span className="k">EPRA last read</span>
            <span className="big">{fp.lastEpra?.at ? dayMonth(fp.lastEpra.at) : '—'}</span>
            <span className="sm" style={{ color: 'var(--faint)' }}>{fp.lastEpra ? 'ceilings are maxima, not pump prices' : 'not on file yet'}</span>
          </div>
        </div>

        {zanaTier && (
          <div className="zcard" style={{ flexDirection: 'row', alignItems: 'center', gap: 12, borderColor: 'var(--z-line)', flexWrap: 'wrap' }}>
            <ZanaMark />
            <span style={{ flex: '1 1 320px' }}>
              {fp.homePin
                ? `Zana can fill your list with stations near the school — up to about 10, priced at the ceiling. Your primary stations and typed prices are never changed.`
                : 'Zana fills your list from stations near the school once the school has a map pin. Ask Trasse to set it.'}
            </span>
            <button type="button" className="btn s b-line" onClick={() => run('nearby')} disabled={!!busy || !fp.homePin}
              style={{ borderColor: 'var(--z-line)', color: 'var(--z-ink)' }}>
              {busy === 'nearby' ? <><span className="spin" aria-hidden="true" style={{ width: 14, height: 14 }} />Zana is searching…</> : fp.placeCount ? 'Refresh nearby stations' : 'Find stations near school'}
            </button>
          </div>
        )}

        <section className="panel" style={{ overflow: 'hidden' }}>
          <div className="ph">
            <div>
              <h2 className="h2">Your stations</h2>
              <p className="sm" style={{ margin: '2px 0 0', color: 'var(--muted)' }}>A fill at one of these uses its price. Anywhere else uses the ceiling — the driver confirms the shillings.</p>
            </div>
            {over.length > 0 && <button type="button" className="btn s b-line" onClick={() => ask(`Why is ${over[0].name} above the EPRA ceiling?`)}>{plural(over.length, 'station')} above the ceiling</button>}
          </div>
          {fp.rows.length === 0 ? (
            <Empty title="No stations yet" body={`Add the stations your buses fill up at, with their price per litre. Until then every fill uses the ${ceilingWord === 'Estimate' ? 'regional estimate' : 'EPRA ceiling'}.`}>
              <button type="button" className="btn s b-ink" onClick={() => setAdding(true)}>Add a station</button>
            </Empty>
          ) : (
            <div className="tblbox">
              <table className="t">
                <thead>
                  <tr><th>Station</th><th>Diesel per litre</th><th>Petrol per litre</th>{zanaTier && <th>Primary</th>}<th /></tr>
                </thead>
                <tbody>
                  {fp.rows.map((r) => (
                    <tr key={r.placeId} style={{ cursor: 'default' }}>
                      <td>
                        <div className="who" style={{ maxWidth: 360 }}>
                          <b style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</b>
                          <span>{r.primary ? 'Primary · ' : ''}{[r.diesel != null ? 'diesel' : '', r.petrol != null ? 'petrol' : ''].filter(Boolean).join(' and ') || 'no price yet'}</span>
                        </div>
                      </td>
                      <td><PriceCell label={`${r.name} diesel price`} value={r.diesel} ceiling={fp.caps.diesel} onSave={(v) => { void fp.setPrice(r.placeId, 'diesel', v).then((x) => say(x, `${r.name} diesel saved`)); }} /></td>
                      <td><PriceCell label={`${r.name} petrol price`} value={r.petrol} ceiling={fp.caps.petrol} onSave={(v) => { void fp.setPrice(r.placeId, 'petrol', v).then((x) => say(x, `${r.name} petrol saved`)); }} /></td>
                      {zanaTier && (
                        <td>
                          <button type="button" className={'chip ' + (r.primary ? 'c-teal' : 'c-mute')} style={{ border: 0, cursor: 'pointer' }} aria-pressed={r.primary}
                            onClick={() => { void fp.setPrimary(r.placeId, !r.primary).then((x) => say(x)); }}>
                            {r.primary ? 'Primary' : 'Make primary'}
                          </button>
                        </td>
                      )}
                      <td className="r"><button type="button" className="btn s b-ghost" style={{ color: 'var(--bad)' }} onClick={() => setRemoving(r)}>Remove</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
        <p className="sm" style={{ margin: 0, color: 'var(--faint)' }}>
          {fp.lastEpra
            ? `EPRA ceilings on file: diesel Ksh ${ksh(fp.lastEpra.diesel)} · petrol Ksh ${ksh(fp.lastEpra.petrol)}. They are maxima, never a pump price. A station on your list keeps the price you typed.`
            : 'EPRA publishes the most a litre may cost each month. Trasse keeps those on file for paid plans; until then fills use a regional estimate.'}
        </p>

        {adding && (
          <AddStation apiKey={fp.apiKey} zanaTier={zanaTier} primaryLeft={Math.max(0, PRIMARY_FUEL_STATION_LIMIT - fp.primaryCount)} ceilings={fp.caps}
            onAdd={(name, placeId, ft, price) => fp.addStation(name, placeId, ft, price).then((r) => { if (r.ok) toast({ kind: 'success', text: `${name.trim()} added` }); return r; })}
            onClose={() => setAdding(false)} />
        )}
        {removing && (
          <ConsoleModal title={`Remove ${removing.name}?`} onClose={() => setRemoving(null)} width={480}
            footer={<ModalButtons cancel="Keep it" onCancel={() => setRemoving(null)} primary="Remove" danger onPrimary={() => { const r = removing; setRemoving(null); void fp.removePlace(r.placeId).then((x) => say(x, `${r.name} removed`)); }} />}>
            <p style={{ margin: 0, fontSize: 16 }}>A fill there will use the ceiling instead of this price.</p>
          </ConsoleModal>
        )}
      </div>
    </PageBody>
  );
};

export default FuelPrices;
